import { useAppSelector } from 'store';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toNumber } from 'lodash';
import { getParitairComiteSelectItemsAction } from 'store/actions/paritair-comite-actions';
import {
  getCompaniesWithExceptionsAction,
  getWageIndexationDataAction,
  getWageIndexationPreviewAction,
  setWageIndexationDataAction,
  setWageIndexationPreviewAction,
} from 'store/actions/wage-indexation-actions';
import { paritairComiteSelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import {
  GENERAL__ALL,
  GENERAL__COLLARTYPE,
  GENERAL__SELECT,
  GENERAL__SHOW,
  MENU__PARITAIR_COMITE,
  PARITAIR_COMITE__END_DATE,
  PARITAIR_COMITE__START_DATE,
  VALIDATION__IS_REQUIRED,
  WAGE_INDEXATION__INDEX_VALUE,
} from 'translations/constants';
import { UPDropdown } from 'components/dropdown';
import UPButton from 'components/button';
import UPDatepicker from 'components/datepicker';
import CurrencyNumberInput from 'components/custom-number-input/CurrencyNumberInput';
import { IMinimumWage } from 'types/minimum-wage';
import WageIndexationPanel from './WageIndexationPanel';
import { WageIndexationFlowContextProps, WageIndexationFilterProps, CompanyExceptionModel } from './types';
import { IndexationFilterContainer, IndexationFlowContainer } from './styles';
import AffectedCompaniesOverViewGrid from './AffectedCompaniesOverViewGrid';
import CompaniesWithExceptionsGrid from './CompaniesWithExceptionsGrid';
import SuccessIndexationModal from './modals/SuccessIndexationModal';

export const WageIndexationFlowContext = createContext<WageIndexationFlowContextProps>({
  wageIndexationProps: null,
  filtersSet: false,
  activeStep: 0,
  companyExceptions: [],
  setCompanyExceptions: () => null,
  updatedMinimumWages: [],
  setUpdatedMinimumWages: () => null,
  onClickPreviousStep: () => null,
  onClickNextStep: () => null,
  isIndexationPerformed: () => null,
});

const WageIndexation: React.FC = () => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const [activeStep, setActiveStep] = useState<number>(0);
  const paritairComiteSelectItems = useAppSelector(paritairComiteSelectItemsSelector);
  const [selectedJointCommission, setSelectedJointCommission] = useState<string>('');
  const [selectedCollarType, setSelectedCollarType] = useState<string>('');
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(null);
  const [indexValue, setIndexValue] = useState<string>('');
  const [indexCurrency, setIndexCurrency] = useState<string>('');
  const [previewPressed, setPreviewPressed] = useState<boolean>(false);
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [updatedMinimumWages, setUpdatedMinimumWages] = useState<IMinimumWage[]>([]);
  const [companyExceptions, setCompanyExceptions] = useState<CompanyExceptionModel[]>([]);
  const [indexationPerformed, setIndexationPerformed] = useState<boolean>(false);

  const collarTypes = useMemo(() => {
    return [
      { label: t(GENERAL__ALL), value: 'ALL' },
      { label: 'Bediende (BED)', value: 'BED' },
      { label: 'Arbeider (ARB)', value: 'ARB' },
    ];
  }, [t]);

  const indexCurrencyList = useMemo(() => {
    return [
      { value: 1, label: '€' },
      { value: 2, label: '%' },
    ];
  }, []);

  const paritairComiteItems = useMemo(() => {
    return paritairComiteSelectItems;
  }, [paritairComiteSelectItems]);

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsAction());
  }, [dispatch]);

  useEffect(() => {
    // On mount disable vertical scrolling
    document.body.style.overflowY = 'hidden';

    return () => {
      // On unmount return the scrolling
      document.body.style.overflowY = 'auto';
    };
  }, []);

  const allSet = useCallback((): boolean => {
    return !!(
      selectedJointCommission &&
      selectedCollarType &&
      indexValue &&
      toNumber(indexValue) !== 0 &&
      indexCurrency
    );
  }, [selectedJointCommission, selectedCollarType, indexValue, indexCurrency]);

  const padTo2Digits = useCallback(num => {
    return num.toString().padStart(2, '0');
  }, []);

  const formatDate = useCallback(
    date => {
      if (date) {
        const auxDate = new Date(date);
        return [auxDate.getFullYear(), padTo2Digits(auxDate.getMonth() + 1), padTo2Digits(auxDate.getDate())].join('-');
      }

      return null;
    },
    [padTo2Digits],
  );

  useEffect(() => {
    const hasDateErrors = !startDate || (startDate && endDate && (startDate >= endDate || endDate <= new Date()));
    const hasIndexErrors = !indexValue || indexValue === '0' || !indexCurrency;
    setHasErrors(hasDateErrors || hasIndexErrors);
  }, [startDate, endDate, indexValue, indexCurrency]);

  const createWageIndexationProps = useCallback((): WageIndexationFilterProps => {
    return {
      jointCommissionId: selectedJointCommission,
      collarType: selectedCollarType,
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
      indexValue: {
        value: parseFloat(indexValue),
        type: toNumber(indexCurrency),
      },
    };
  }, [selectedJointCommission, selectedCollarType, startDate, endDate, indexValue, indexCurrency, formatDate]);

  const onClickPreview = useCallback(() => {
    setPreviewPressed(true);
    setIndexationPerformed(false);
    setActiveStep(0);
    if (allSet()) {
      dispatch(
        getWageIndexationDataAction({
          jointCommissionId: selectedJointCommission,
          collarType: selectedCollarType,
          indexValue: { value: parseFloat(indexValue), type: toNumber(indexCurrency) },
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
        }),
      );
      dispatch(getWageIndexationPreviewAction(createWageIndexationProps()));
      dispatch(
        getCompaniesWithExceptionsAction({
          jointCommissionId: toNumber(selectedJointCommission),
          collarType: selectedCollarType,
          index: { value: parseFloat(indexValue), type: toNumber(indexCurrency) },
          startDate: formatDate(startDate),
          endDate: formatDate(endDate),
        }),
      );
    }
  }, [
    selectedJointCommission,
    selectedCollarType,
    startDate,
    endDate,
    formatDate,
    dispatch,
    allSet,
    createWageIndexationProps,
    indexValue,
    indexCurrency,
  ]);

  const resetWageGridValues = useCallback(() => {
    dispatch(setWageIndexationDataAction([]));
    dispatch(setWageIndexationPreviewAction([]));
    setActiveStep(0);
  }, [dispatch]);

  useEffect(() => {
    if (!allSet()) {
      resetWageGridValues();
    }
  }, [allSet, resetWageGridValues]);

  const checkNumberInputError = useCallback(() => {
    return (!indexValue || indexValue === '0' || !indexCurrency) && previewPressed;
  }, [indexCurrency, indexValue, previewPressed]);

  const onClickPreviousStep = useCallback(() => {
    document.querySelectorAll('*').forEach(el => {
      if (el.scrollHeight > el.clientHeight) {
        el.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
    setActiveStep(activeStep - 1);
  }, [activeStep]);

  const onClickNextStep = useCallback(() => {
    setActiveStep(activeStep + 1);
  }, [activeStep]);

  const resetActiveStep = useCallback(() => {
    setActiveStep(0);
    resetWageGridValues();
  }, [resetWageGridValues]);

  const isIndexationPerformed = useCallback(() => {
    setIndexationPerformed(true);
  }, []);

  // after the indexation is performed, we show a success alert for 5 seconds
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (indexationPerformed) {
      timer = setTimeout(() => {
        setIndexationPerformed(false);
        resetActiveStep();
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [indexationPerformed, resetActiveStep]);

  const contextValues = useMemo((): WageIndexationFlowContextProps => {
    return {
      wageIndexationProps: createWageIndexationProps(),
      filtersSet: allSet(),
      companyExceptions,
      setCompanyExceptions,
      updatedMinimumWages,
      setUpdatedMinimumWages,
      activeStep,
      onClickPreviousStep,
      onClickNextStep,
      isIndexationPerformed,
    };
  }, [
    createWageIndexationProps,
    allSet,
    activeStep,
    companyExceptions,
    updatedMinimumWages,
    onClickPreviousStep,
    onClickNextStep,
    isIndexationPerformed,
  ]);

  return (
    <>
      {indexationPerformed && (
        <SuccessIndexationModal
          isOpen={indexationPerformed}
          onClose={() => {
            setIndexationPerformed(false);
            resetActiveStep();
          }}
        />
      )}
      <IndexationFilterContainer id="wageindexation-filters">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box width="220px">
            <UPDropdown
              required
              label={t(MENU__PARITAIR_COMITE)}
              placeholder={t(GENERAL__SELECT)}
              items={paritairComiteItems}
              value={selectedJointCommission}
              onChange={event => {
                const { value } = event.target;
                setSelectedJointCommission(value);
                setPreviewPressed(false);
                resetWageGridValues();
              }}
              error={!selectedJointCommission && previewPressed}
              helperText={t(VALIDATION__IS_REQUIRED)}
            />
          </Box>
          <Box width="220px">
            <UPDropdown
              required
              label={t(GENERAL__COLLARTYPE)}
              placeholder={t(GENERAL__SELECT)}
              items={collarTypes}
              value={selectedCollarType}
              onChange={event => {
                const { value } = event.target;
                setSelectedCollarType(value);
                setPreviewPressed(false);
                resetWageGridValues();
              }}
              error={!selectedCollarType && previewPressed}
              helperText={t(VALIDATION__IS_REQUIRED)}
            />
          </Box>
          <Box width="220px">
            <CurrencyNumberInput
              value={indexValue}
              currencyType={indexCurrency}
              onChangeCurrencyType={e => {
                setIndexCurrency(e);
                setPreviewPressed(false);
                resetWageGridValues();
              }}
              currencyTypeList={indexCurrencyList}
              label={t(WAGE_INDEXATION__INDEX_VALUE)}
              onChangeValue={e => {
                setIndexValue(e);
                setPreviewPressed(false);
                resetWageGridValues();
              }}
              error={checkNumberInputError()}
              helperText={t(VALIDATION__IS_REQUIRED)}
            />
          </Box>
          <Box width="220px">
            <UPDatepicker
              required
              label={t(PARITAIR_COMITE__START_DATE)}
              value={startDate}
              onChange={e => {
                setStartDate(e);
                setPreviewPressed(false);
              }}
              error={!startDate && previewPressed}
              helperText={t(VALIDATION__IS_REQUIRED)}
              maxDate={endDate}
            />
          </Box>
          <Box width="220px">
            <UPDatepicker
              label={t(PARITAIR_COMITE__END_DATE)}
              value={endDate}
              onChange={e => setEndDate(e)}
              minDate={startDate}
            />
          </Box>
          <Box width="220px">
            <Box sx={{ marginTop: '1rem' }}>
              <UPButton text={t(GENERAL__SHOW)} onClick={() => onClickPreview()} disabled={hasErrors} />
            </Box>
          </Box>
        </Box>
      </IndexationFilterContainer>
      <WageIndexationFlowContext.Provider value={contextValues}>
        <Box sx={{ height: '90vh', overflowX: 'auto', overflowY: 'hidden' }}>
          <IndexationFlowContainer>
            <WageIndexationPanel />
            {selectedCollarType === 'ALL' && <CompaniesWithExceptionsGrid />}
            <AffectedCompaniesOverViewGrid />
          </IndexationFlowContainer>
        </Box>
      </WageIndexationFlowContext.Provider>
    </>
  );
};

export default WageIndexation;
